import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroupErrorMessageComponent, LoadingPanelService, noWhitespaceValidator, PASSWORD_PATTERN, Utils } from 'lib-base';
import { firstValueFrom, forkJoin, Subscription } from 'rxjs';
import { SecUsuarioService } from '../../../services/database/secUsuario.service';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { RecaptchaV3Module, ReCaptchaV3Service } from 'ng-recaptcha';
import { ActivarUsuarioRequest } from '../../../../../../tramitantes/src/app/shared/interfaces/seguridad';
import { CommonModule } from '@angular/common';
import { PasswordModule } from 'primeng/password';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { DialogModule } from 'primeng/dialog';

@Component({
  selector: 'app-activar-usuario',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, FormGroupErrorMessageComponent, PasswordModule, ButtonModule, DividerModule,
    DialogModule, RecaptchaV3Module],
  templateUrl: './activar-usuario.component.html',
  styleUrl: './activar-usuario.component.scss'
})
export class ActivarUsuarioComponent implements OnInit, OnDestroy {

  #codigo !: string;
  isCodeValid: boolean = false;
  currentTime !: Date;
  location!: string;
  form !: FormGroup;
  translations: any = {};
  recaptchaSubscription !: Subscription;

  constructor(private _route: ActivatedRoute, private _fb: FormBuilder, private _sus: SecUsuarioService, private _ms: MessageService,
    private _t: TranslateService, private _r: Router, private recaptchaV3Service: ReCaptchaV3Service, private _lps: LoadingPanelService
  ) {
    this.#codigo = this._route.snapshot.params['codigo'];
    this.configureForm();
  }

  ngOnDestroy(): void {
    if (this.recaptchaSubscription) {
      this.recaptchaSubscription.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.translate();
    this.updateLocation();
    this.updateTime();
    this.isCodigoActivacionValido();
  }

  private async translate(): Promise<void> {
    this.translations = await firstValueFrom(forkJoin({
      usuarioActivado: this._t.get('msg.usuarioActivado'),
      exito: this._t.get('common.exito'),
      CodigoNoValido: this._t.get('msg.CodigoNoValido'),
      warnSummary: this._t.get('form.msg.warn-summary'),
      completeCampos: this._t.get('form.msg.complete-campos'),
      campoRequerido: this._t.get('form.msg.campo-requerido'),
      sugerencias: this._t.get('form.password.sugerencias'),
      contrasena: this._t.get('form.password.contrasena'),
      constasenaNoCoiniciden: this._t.get('form.password.constasena-no-coiniciden'),
      repitaContrasena: this._t.get('form.password.repita-contraseña'),
      alMenosUnaMinuscula: this._t.get('form.password.al-menos-una-minuscula'),
      alMenosUnaMayuscula: this._t.get('form.password.al-menos-una-mayuscula'),
      alMenosUnNumero: this._t.get('form.password.al-menos-un-número'),
      minimoCaracteres: this._t.get('form.password.minimo-caracteres'),
      maximoCaracteres: this._t.get('form.password.maximo-caracteres'),
      alMenosUnCaracterEspecial: this._t.get('form.password.al-menos-un-caracter-especial'),
      cambiarContrasena: this._t.get('form.password.cambiar-contrasena'),
      welcome: this._t.get('common.bienvenido'),
    }));
  }

  configureForm() {
    this.form = this._fb.group({
      password: [null, [Validators.required, noWhitespaceValidator, Validators.pattern(PASSWORD_PATTERN), Validators.maxLength(200)]],
      passwordRepeat: [null, [Validators.required, noWhitespaceValidator]],
    }, { validators: this.passwordMatchValidator });
  }

  private passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    return control.value.password === control.value.passwordRepeat ? null : { notMatch: true };
  }

  onSubmit() {
    if (this.form.invalid) {
      Utils.markAllFormControlsAsTouched(this.form);
      this._ms.add({ severity: 'warn', summary: this.translations.warnSummary, detail: this.translations.completeCampos });
      return;
    }

    this.recaptchaSubscription = this.recaptchaV3Service.execute('activarUsuarioFuncionario')
      .subscribe((token) => {
        const request = {
          codigo: this.#codigo,
          newPassword: this.form.get('password')?.value,
          captchaToken: token
        } as ActivarUsuarioRequest;

        this.activarUsuario(request);
      });
  }
  updateTime(): void {
    this.currentTime = new Date();
  }

  updateLocation(): void {
    const now = new Date();
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const offset = -now.getTimezoneOffset() / 60;
    const offsetString = offset >= 0 ? `+${offset}:00` : `${offset}:00`;
    this.location = `${timeZone} ${offsetString}`;
  }

  //#region API Calls
  activarUsuario(request: ActivarUsuarioRequest) {
    this._lps.show(true);
    this._sus.activarUsuario(request).subscribe({
      next: (response: boolean) => {
        this._ms.add({ severity: 'success', summary: this.translations.exito, detail: this.translations.usuarioActivado });
        this._r.navigate(['/public/login']);
        this._lps.hide();
      }
    });
  }

  isCodigoActivacionValido() {
    this._sus.isCodigoActivacionValido(this.#codigo).subscribe({
      next: (response: boolean) => {
        this.isCodeValid = response;
        if (response) {
          return;
        }
        this._ms.add({ severity: 'warn', summary: this.translations.warnSummary, detail: this.translations.CodigoNoValido });
        this._r.navigate(['/public/login']);
      }
    });
  }
  //#endregion API Calls
}
